// src/components/achievements/AchievementRanking.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import NavLink from './../common/NavLink';
import { slugify } from '../../utils/slugify'; 
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const AchievementRanking = () => {
  const { category } = useParams();
  const [metadata, setMetadata] = useState({
    title: 'Ranking',
    color: 'white',
    mainColumnName: null,
    extraColumns: [],
    type: 'team'
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/achievements/${category}?limit=100`)
      .then(res => res.json())
      .then(result => {
        let extraCols = [];
        if (result.metadata?.extraColumns) {
          if (Array.isArray(result.metadata.extraColumns)) {
            extraCols = result.metadata.extraColumns;
          } else if (result.metadata.extraColumns.value) {
            try {
              extraCols = JSON.parse(result.metadata.extraColumns.value);
            } catch (e) {
              console.warn('Błąd parsowania extraColumns', e);
            }
          }
        }

        setMetadata({
          title: result.metadata?.title || 'Ranking',
          color: result.metadata?.color || 'white',
          mainColumnName: result.metadata?.mainColumnName || null,
          extraColumns: extraCols,
          type: result.metadata?.type || 'team'
        });
        setData(result.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Błąd:', err);
        setLoading(false);
      });
  }, [category]);

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><Typography>Ładowanie...</Typography></Box>;
  if (data.length === 0) return <Box sx={{ textAlign: 'center', py: 8 }}><Typography>Brak danych</Typography></Box>;

  const { title, color, mainColumnName, extraColumns, type } = metadata;
  const isLeague = type === 'league';

  const mainValueKey = Object.keys(data[0] || {}).find(key =>
    !['rank', 'teamId', 'teamName', 'leagueName', 'leagueGroup', 'matches', 'competitionId'].includes(key) &&
    !extraColumns.some(col => col.key === key)
  );

  const mainColumnTitle = mainColumnName || (mainValueKey ? mainValueKey.replace(/_/g, ' ') : '');

  const getColorValue = () => {
    if (color === 'green') return '#4caf50';
    if (color === 'red') return '#f44336';
    return '#ffffff';
  };

  const hasMatches = data.some(row => row.matches != null);

  return (
    <Box sx={{ maxWidth: { xs: '100%', md: 900 }, mx: 'auto', px: { xs: 0, md: 2 }, py: 2 }}>
      <Typography variant="h5" align="center" gutterBottom sx={{ mb: 4, fontWeight: 700, color: '#fff' }}>
        {title}
      </Typography>

      <TableContainer component={Paper} sx={{ bgcolor: '#1a1a1a', borderRadius: 2, overflow: 'hidden' }}>
        <Table size="dense" sx={{ '& .MuiTableRow-root': { height: { xs: 42, md: 48 } } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#111' }}>
              <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' }, width: 20 }}>#</TableCell>
              
              {!isLeague ? (
                <TableCell sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' } }}>Drużyna</TableCell>
              ) : null}
			  
			  {!isLeague ? (
              <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' }, width: 180 }}>Liga</TableCell>
			  ) : <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' }, width: 220 }}>Liga</TableCell>}

              {isLeague ? (
                <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' }, width: 200 }}>Grupa</TableCell>
              ) : null}

              {hasMatches ? (
                <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' }, width: 60 }}>Mecze</TableCell>
              ) : null}

              {mainValueKey && (
                <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
                  {mainColumnTitle}
                </TableCell>
              )}

              {extraColumns.map((col, idx) => (
                <TableCell key={idx} align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
                  {col.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => {
              const leagueDisplay = row.leagueGroup && row.leagueGroup !== 'Brak' && row.leagueGroup.trim() !== ''
                ? `${row.leagueName} (${row.leagueGroup})`
                : row.leagueName;

              const groupDisplay = row.leagueGroup && row.leagueGroup !== 'Brak' && row.leagueGroup.trim() !== ''
                ? row.leagueGroup
                : '';
			  

              return (
                <TableRow key={row.teamId || row.competitionId} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                  <TableCell align="center">
                    <Typography fontWeight={700} color="#fff" sx={{ fontSize: { xs: '0.75rem', md: '0.95rem' } }}>
                      {row.rank}
                    </Typography>
                  </TableCell>

                  {/* Drużyna – tylko dla klubowych */}
                  {!isLeague && (
                    <TableCell>
                      <Typography
                        component={NavLink}
                        to={`/team/${row.teamId}/${slugify(row.teamName)}`}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: { xs: '0.75rem', md: '0.9rem' },
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {row.teamName}
                      </Typography>
                    </TableCell>
                  )}
					
                  {/* Liga – formatowanie jak Drużyna w klubowych */}
                  <TableCell align="center">
                    <Typography
                      component={NavLink}
                      to={`/liga/${row.competitionId}/${slugify(leagueDisplay)}`}
                      sx={{
                        color: 'white',
                          fontWeight: 600,
                          fontSize: { xs: '0.8rem', md: '0.95rem' },
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {row.leagueName}
                    </Typography>
                  </TableCell>

                  {/* Grupa – tylko dla ligowych, mniejsza */}
                  {isLeague && (
                    <TableCell align="center">
                      <Typography
					  component={NavLink}
					  to={`/liga/${row.competitionId}`}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: { xs: '0.75rem', md: '0.9rem' },
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {groupDisplay}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Mecze – zawsze gdy istnieje */}
                  {hasMatches && (
                    <TableCell align="center">
                      <Typography fontWeight={600} color="#ccc" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
                        {row.matches ?? '-'}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Główna wartość */}
                  {mainValueKey && (
                    <TableCell align="center">
                      <Typography
                        fontWeight={900}
                        sx={{
                          fontSize: { xs: '0.9rem', md: '1.1rem' },
                          color: getColorValue(),
                          textShadow: color === 'green' ? '0 0 6px rgba(76,175,80,0.4)' :
                                      color === 'red' ? '0 0 6px rgba(244,67,54,0.4)' : 'none',
                        }}
                      >
                        {row[mainValueKey]}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Extra kolumny */}
                  {extraColumns.map((col, idx) => (
                    <TableCell key={idx} align="center">
                      <Typography fontWeight={600} color="#ccc" sx={{ fontSize: { xs: '0.75rem', md: '0.9rem' } }}>
                        {row[col.key] ?? '-'}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AchievementRanking;